import { IImageComponentResponse } from "@customTypes/componentResponses";
import useAppContext from "@hooks/useAppContext";
import ReviewBackgroundElements from "@libs/cards/assets/ReviewBackgroundElements";
import DesktopReviewCardNoImages from "./components/ReviewCard/DesktopReviewCardNoImages";
import DesktopReviewCardWithImages from "./components/ReviewCard/DesktopReviewCardWithImages";
import MobileReviewCard from "./components/ReviewCard/MobileReviewCard";

type LeftOffsetType = "10px" | "20px";
interface IReviewCardSliderWrapper {
  leftOffset?: LeftOffsetType;
  children: JSX.Element;
}

export interface IReviewCardProps {
  review: string | JSX.Element | JSX.Element[];
  headshot?: string | IImageComponentResponse[] | null;
  country: string;
  date?: string | null;
  reviewer: string;
  images?: IImageComponentResponse[];
  hasGallery?: boolean;
}

const ReviewCard = ({
  review,
  headshot,
  reviewer,
  country,
  date,
  images,
  hasGallery,
}: IReviewCardProps): JSX.Element => {
  const { isMobile } = useAppContext();
  return isMobile ? (
    <MobileReviewCard
      review={review}
      headshot={headshot}
      reviewer={reviewer}
      country={country}
      date={date}
      images={images}
    />
  ) : hasGallery ? (
    <DesktopReviewCardWithImages
      review={review}
      headshot={headshot}
      reviewer={reviewer}
      country={country}
      date={date}
      images={images}
    />
  ) : (
    <DesktopReviewCardNoImages
      review={review}
      headshot={headshot}
      reviewer={reviewer}
      country={country}
      date={date}
    />
  );
};

const LEFT_OFFSET = {
  "10px": "-left-[0.625rem]",
  "20px": "-left-[1.25rem]",
};

export const ReviewCardBackground = ({
  leftOffset,
}: {
  leftOffset?: LeftOffsetType;
}): JSX.Element => {
  return (
    <div
      className={`absolute block flex h-full flex-col justify-between pt-10 ${
        LEFT_OFFSET[leftOffset ?? "20px"]
      } desktop:hidden`}
    >
      <ReviewBackgroundElements variant="top" />
      <div className="relative flex-grow">
        {/* Extra div added to remove the very thin white stipe between the components */}
        <div className="absolute top-[-1%] h-[102%] w-full bg-blue-5" />
      </div>
      <ReviewBackgroundElements variant="bottom" />
    </div>
  );
};

export const ReviewCardSliderWrapper = ({
  children,
  leftOffset,
}: IReviewCardSliderWrapper): JSX.Element => {
  return (
    <div className="relative min-h-[32.5rem] desktop:pb-[1rem]">
      <ReviewCardBackground leftOffset={leftOffset} />
      {children}
    </div>
  );
};

export default ReviewCard;
